import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Step1CollectInfo from './Step1_CollectInfo';
import Step2DoqSolv from './Step2_DoqSolv';
import Step3Docustamp from './Step3_Docustamp';
import Step4ESign from './Step4_eSign';
import Step5Success from './Step5_Success';
import './LoanAppDemo.css';

const STEPS = [
  { id: 1, label: 'Your Info', api: null },
  { id: 2, label: 'DoqSolv', api: 'doqsolv' },
  { id: 3, label: 'DocuStamp', api: 'docustamp' },
  { id: 4, label: 'Aadhaar eSign', api: 'esign' },
  { id: 5, label: 'Complete', api: null },
];

/**
 * Session Storage Keys — used to persist state across the eSign redirect.
 */
const SS_FORM = 'loanDemo_formData';
const SS_STATE = 'loanDemo_demoState';
const SS_REDIRECT_FLAG = 'loanDemo_esignRedirect';

const LoanAppDemo = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [theme, setTheme] = useState('light');
  const [currentStep, setCurrentStep] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const redirectHandled = useRef(false);
  const [formData, setFormData] = useState({});

  // Shared state passed between steps for real API data
  const [demoState, setDemoState] = useState({
    // Template variables schema (fetched from DoqSolv)
    templateVariables: [],

    // Step 2 — DoqSolv
    documentId: null,
    pdfFileId: null,
    templateName: '',
    agreementPdfBytes: null,

    // Step 3 — DocuStamp
    stampDocumentId: null,
    stampCertificateId: null,
    stampPdfBytes: null,
    mergedPdfBytes: null,

    // Step 4 — eSign
    envelopeId: null,
    esignUrl: null,
    esignStatus: null,

    // Step 4 — signer email (persisted for email send)
    signerEmail: null,
  });

  // ─── Handle eSign redirect return ──────────────────────────────
  // Two detection strategies (belt-and-suspenders):
  //  1. Check for `esign_status` query parameter (our redirect_url)
  //  2. Check for the redirect flag in sessionStorage (failsafe)
  // This ensures the user always lands on Step 5 after completing eSign,
  // even if Meon/NSDL strips or modifies our query parameters.
  useEffect(() => {
    if (redirectHandled.current) return;

    const esignStatus = searchParams.get('esign_status');
    const hasRedirectFlag = sessionStorage.getItem(SS_REDIRECT_FLAG);
    const savedForm = sessionStorage.getItem(SS_FORM);
    const savedState = sessionStorage.getItem(SS_STATE);

    // Only handle if we have evidence of a redirect
    if (!esignStatus && !hasRedirectFlag) return;

    redirectHandled.current = true;

    // Determine the outcome
    const status = esignStatus || 'success'; // Default to success if we have the flag

    // Restore persisted data
    if (savedForm) {
      try { setFormData(JSON.parse(savedForm)); } catch (e) { /* ignore */ }
    }
    if (savedState) {
      try {
        const restored = JSON.parse(savedState);
        setDemoState(prev => ({ ...prev, ...restored, esignStatus: status }));
      } catch (e) { /* ignore */ }
    } else {
      setDemoState(prev => ({ ...prev, esignStatus: status }));
    }

    // Jump to Step 5 (success) or Step 4 (cancelled)
    if (status === 'cancelled') {
      setCurrentStep(4);
    } else {
      // Any non-cancelled status (success, signed, completed, etc.) → Step 5
      setCurrentStep(5);
    }

    // Clean up
    sessionStorage.removeItem(SS_FORM);
    sessionStorage.removeItem(SS_STATE);
    sessionStorage.removeItem(SS_REDIRECT_FLAG);

    // Remove query params from URL (clean browser bar)
    if (esignStatus) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const goToStep = useCallback((step) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(step);
      setIsTransitioning(false);
    }, 300);
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < 5) goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  const handleRestart = useCallback(() => {
    setFormData({});
    setDemoState({
      templateVariables: [],
      documentId: null, pdfFileId: null, templateName: '',
      agreementPdfBytes: null, stampDocumentId: null,
      stampCertificateId: null, stampPdfBytes: null,
      mergedPdfBytes: null, envelopeId: null, esignUrl: null,
      esignStatus: null, signerEmail: null,
    });
    goToStep(1);
  }, [goToStep]);

  /**
   * Save state to sessionStorage before eSign redirect.
   * Also sets a redirect flag as a failsafe detection mechanism.
   */
  const saveStateForRedirect = useCallback(() => {
    sessionStorage.setItem(SS_FORM, JSON.stringify(formData));
    sessionStorage.setItem(SS_STATE, JSON.stringify({
      documentId: demoState.documentId,
      stampDocumentId: demoState.stampDocumentId,
      stampCertificateId: demoState.stampCertificateId,
      envelopeId: demoState.envelopeId,
      templateName: demoState.templateName,
      signerEmail: demoState.signerEmail,
    }));
    // Failsafe flag — even if Meon strips our query params,
    // we know the user was redirected for eSign
    sessionStorage.setItem(SS_REDIRECT_FLAG, 'true');
  }, [formData, demoState]);

  const renderStep = () => {
    const props = {
      formData,
      setFormData,
      onNext: handleNext,
      demoState,
      setDemoState,
      saveStateForRedirect,
    };

    switch (currentStep) {
      case 1: return <Step1CollectInfo {...props} />;
      case 2: return <Step2DoqSolv {...props} />;
      case 3: return <Step3Docustamp {...props} />;
      case 4: return <Step4ESign {...props} />;
      case 5: return <Step5Success formData={formData} demoState={demoState} onRestart={handleRestart} />;
      default: return null;
    }
  };

  return (
    <div className="loan-demo-root" data-loan-theme={theme}>
      {/* ── Navbar ──────────────────────────────────────── */}
      <nav className="loan-navbar">
        <div className="loan-navbar__brand">
          <div className="loan-navbar__logo">NB</div>
          <span className="loan-navbar__name">NeoBank</span>
        </div>
        <div className="loan-navbar__actions">
          <button
            className="loan-back-link"
            onClick={() => navigate('/')}
            title="Back to main app"
          >
            ← Back
          </button>
          <button
            className="loan-theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            <span className="loan-theme-toggle__knob">
              {theme === 'light' ? '☀️' : '🌙'}
            </span>
          </button>
        </div>
      </nav>

      {/* ── Progress Bar ───────────────────────────────── */}
      <div className="loan-progress">
        {STEPS.map((step) => {
          const isComplete = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          let fillPercent = 0;
          if (isComplete) fillPercent = 100;
          else if (isCurrent) fillPercent = 30;

          return (
            <div className="loan-progress__segment" key={step.id} title={step.label}>
              <div
                className="loan-progress__fill"
                style={{
                  width: `${fillPercent}%`,
                  background: isComplete
                    ? 'var(--loan-success)'
                    : isCurrent
                    ? 'var(--loan-accent)'
                    : undefined,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* ── Step Content ───────────────────────────────── */}
      <div className="loan-step-container">
        <div className={`loan-step ${isTransitioning ? 'loan-step--exiting' : ''}`} key={currentStep}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default LoanAppDemo;
